import React, { useMemo } from "react";

import { AlertTriangle, ArrowLeft, Loader2, RefreshCw } from "lucide-react";

import { ExperimentParticipantPreview } from "./ExperimentParticipantPreview";
import {
  getExperimentApiErrorMessage,
  resolvePrototypeIterationIdFromDetail,
  useFeedbackFlowDeploymentStatus,
  usePrototypeByExperiment,
} from "../../api/experimentsApi";
import { useFeedbackFlow, getFeedbackFlowErrorMessage } from "../../api/feedbackFlowApi";
import { getQuestionsBySection } from "../../utils/feedbackFlow";

type PersistedExperimentParticipantPreviewProps = {
  experimentId?: string | null;
  initialRoute?: string;
  mode: React.ComponentProps<typeof ExperimentParticipantPreview>["mode"];
  onBack: () => void;
  onFinish: React.ComponentProps<typeof ExperimentParticipantPreview>["onFinish"];
};

function ParticipantPreviewStateScreen({
  title,
  message,
  actionLabel,
  onAction,
  loading = false,
  onRetry,
}: {
  title: string;
  message: string;
  actionLabel: string;
  onAction: () => void;
  loading?: boolean;
  onRetry?: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 px-6"
      data-testid="participant-preview-state"
    >
      <div className="max-w-md rounded-3xl border border-[#D9B64B]/18 bg-[linear-gradient(180deg,rgba(14,14,14,0.98),rgba(5,5,5,0.98))] p-8 text-center text-white shadow-[0_28px_90px_rgba(0,0,0,0.45)]">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#D9B64B]/18 bg-[rgba(217,182,75,0.08)] text-[#D9B64B]">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <AlertTriangle className="h-6 w-6" />
          )}
        </div>
        <h1 className="mt-5 text-xl font-semibold">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-slate-400">{message}</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-2 rounded-lg border border-[#D9B64B]/18 bg-white/5 px-4 py-2 text-sm font-medium text-slate-100 transition-colors hover:bg-white/10"
            >
              <RefreshCw className="h-4 w-4 text-[#D9B64B]" />
              Retry
            </button>
          ) : null}
          <button
            type="button"
            onClick={onAction}
            className="inline-flex items-center gap-2 rounded-lg bg-[#D9B64B] px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-[#E7C96C]"
          >
            <ArrowLeft className="h-4 w-4" />
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PersistedExperimentParticipantPreview({
  experimentId,
  initialRoute,
  mode,
  onBack,
  onFinish,
}: PersistedExperimentParticipantPreviewProps) {
  const normalizedExperimentId = experimentId?.trim() || undefined;
  const prototypeQuery = usePrototypeByExperiment(normalizedExperimentId, {
    enabled: Boolean(normalizedExperimentId),
  });
  const iterationId = useMemo(
    () => resolvePrototypeIterationIdFromDetail(prototypeQuery.data),
    [prototypeQuery.data],
  );
  const feedbackFlowQuery = useFeedbackFlow(normalizedExperimentId, iterationId, {
    enabled: Boolean(normalizedExperimentId && iterationId),
  });
  const deploymentStatusQuery = useFeedbackFlowDeploymentStatus(
    normalizedExperimentId,
    iterationId ?? undefined,
    {
      enabled: Boolean(normalizedExperimentId && iterationId),
    },
  );

  const previewBaseUrl =
    deploymentStatusQuery.data?.data?.publicUrl?.trim() ||
    prototypeQuery.data?.publicUrl?.trim() ||
    undefined;
  const feedbackFlow = feedbackFlowQuery.data?.feedbackFlow;
  const prototypeQuestionCount = feedbackFlow
    ? getQuestionsBySection(feedbackFlow, "prototype").length
    : 0;

  if (!normalizedExperimentId) {
    return (
      <ParticipantPreviewStateScreen
        title="Participant preview unavailable"
        message="This preview needs a saved experiment id before it can load persisted feedback flow data."
        actionLabel={mode === "embedded" ? "Back to Tagging" : "Back to Innovation"}
        onAction={onBack}
      />
    );
  }

  const isLoading =
    prototypeQuery.isLoading ||
    (Boolean(iterationId) && feedbackFlowQuery.isLoading) ||
    (Boolean(iterationId) && deploymentStatusQuery.isLoading);

  if (isLoading) {
    return (
      <ParticipantPreviewStateScreen
        title="Loading participant preview"
        message="Fetching the saved prototype deployment and feedback flow details from the API."
        actionLabel={mode === "embedded" ? "Back to Tagging" : "Back to Innovation"}
        onAction={onBack}
        loading
      />
    );
  }

  const prototypeError = prototypeQuery.error
    ? getExperimentApiErrorMessage(
        prototypeQuery.error,
        "Unable to load prototype details for this preview.",
      )
    : null;
  const feedbackFlowError = feedbackFlowQuery.error
    ? getFeedbackFlowErrorMessage(
        feedbackFlowQuery.error,
        "Unable to load the saved feedback flow for this preview.",
      )
    : null;
  const deploymentStatusError = deploymentStatusQuery.error
    ? getExperimentApiErrorMessage(
        deploymentStatusQuery.error,
        "Unable to load the participant preview deployment details.",
      )
    : null;
  const errorMessage = prototypeError || feedbackFlowError || deploymentStatusError;

  if (errorMessage) {
    const retry = () => {
      void prototypeQuery.refetch();
      if (iterationId) {
        void feedbackFlowQuery.refetch();
        void deploymentStatusQuery.refetch();
      }
    };

    return (
      <ParticipantPreviewStateScreen
        title="Unable to load participant preview"
        message={errorMessage}
        actionLabel={mode === "embedded" ? "Back to Tagging" : "Back to Innovation"}
        onAction={onBack}
        onRetry={retry}
      />
    );
  }

  if (!iterationId) {
    return (
      <ParticipantPreviewStateScreen
        title="Participant preview not ready"
        message="The preview needs a saved prototype iteration before persisted participant details can be loaded."
        actionLabel={mode === "embedded" ? "Back to Tagging" : "Back to Innovation"}
        onAction={onBack}
      />
    );
  }

  if (!feedbackFlow) {
    return (
      <ParticipantPreviewStateScreen
        title="Participant preview not ready"
        message="The API did not return feedback flow data for this saved iteration yet."
        actionLabel={mode === "embedded" ? "Back to Tagging" : "Back to Innovation"}
        onAction={onBack}
      />
    );
  }

  return (
    <ExperimentParticipantPreview
      feedbackFlow={feedbackFlow}
      initialRoute={initialRoute}
      previewBaseUrl={previewBaseUrl}
      mode={mode}
      onBack={onBack}
      onFinish={onFinish}
      hasPrototypePreview={prototypeQuestionCount === 0 || Boolean(previewBaseUrl)}
    />
  );
}

export default PersistedExperimentParticipantPreview;
